import Vue from 'vue';
import Vuex, { StoreOptions } from 'vuex';
import searchPlugin from 'vuex-search';
import Messages from '@/ts/store/modules/Messages';
import User from '@/ts/store/modules/User';
import Camera from '@/ts/store/modules/Camera';
import Images from '@/ts/store/modules/Images';
import Viewport from '@/ts/store/modules/Viewport';
import Notifications from '@/ts/store/modules/Notifications';
import Attachments from '@/ts/store/modules/Attachments';
import Events from '@/ts/store/modules/Events';
import UserTickets from '@/ts/store/modules/UserTickets';
import Options from '@/ts/store/modules/Options';
import Documents from '@/ts/store/modules/Documents';
import Tags from '@/ts/store/modules/Tags';
import Categories from '@/ts/store/modules/Categories';
import Socket from '@/ts/store/modules/Socket';
import Feedback from '@/ts/store/modules/Feedback';
import SharedFacilities from '@/ts/store/modules/SharedFacilities';
import StaffProperties from '@/ts/store/modules/StaffProperties';
import Likes from '@/ts/store/modules/Likes';
import HomeAssistant from '@/ts/store/modules/HomeAssistant';
import IssueReport from '@/ts/store/modules/IssueReport';
import UserTicketsAttributes from '@/ts/store/modules/UserTicketsAttributes';
import Loading from '@/ts/store/modules/Loading';
import TenantNotifications from '@/ts/store/modules/TenantNotifications';
import CreditCards from '@/ts/store/modules/CreditCards';
import CorpTransactions from '@/ts/store/modules/CorpTransactions';
import UserWallet from '@/ts/store/modules/UserWallet';
import FinanceControl from '@/ts/store/modules/FinanceControl';
import CorporationLinks from '@/ts/store/modules/CorporationLinks';

// Admin
import Corporations from '@/ts/store/modules/Corporations';
import Projects from '@/ts/store/modules/Projects';
import Buildings from '@/ts/store/modules/Buildings';
import UserManagement from '@/ts/store/modules/UserManagement';
import Properties from '@/ts/store/modules/Properties';
import Variants from '@/ts/store/modules/Variants';
import Editor from '@/ts/store/modules/Editor';
import Tickets from '@/ts/store/modules/Tickets';
import TicketAttributes from '@/ts/store/modules/TicketAttributes';
import WaitingLists from '@/ts/store/modules/WaitingLists';
import EventLogs from '@/ts/store/modules/EventLogs';
import PresentationFlows from '@/ts/store/modules/PresentationFlows';
import Integrations from '@/ts/store/modules/Integrations';
import UserProfiles from '@/ts/store/modules/UserProfiles';
import ConfirmPopup from '@/ts/store/modules/ConfirmPopup';
import Localization from '@/ts/store/modules/Localization';
import RolesAndPermissions from '@/ts/store/modules/RolesAndPermissions';
import Recipients from '@/ts/store/modules/Recipients';
import PropertyNotes from '@/ts/store/modules/PropertyNotes';
import Groupings from '@/ts/store/modules/Groupings';
import TableConfigs from '@/ts/store/modules/TableConfigs';
import UnitGroupingCharts from '@/ts/store/modules/UnitGroupingCharts';
import TemplateRenderer from '@/ts/store/modules/TemplateRenderer';
import Identities from '@/ts/store/modules/Identities';
import FileUpload from '@/ts/store/modules/FileUpload';
import Comments from '@/ts/store/modules/Comments';
import Overlays from '@/ts/store/modules/Overlays';
import GlobalOverlay from '@/ts/store/modules/GlobalOverlay';
import Scroll from '@/ts/store/modules/Scroll';
import Abuse from '@/ts/store/modules/Abuse';
import UnitFinance from '@/ts/store/modules/UnitFinance';
import UnpaidPayments from '@/ts/store/modules/UnpaidPayments';
import CreateDepositForm from '@/ts/store/modules/CreateDepositForm';
import UserUpcomingPayments from '@/ts/store/modules/UserUpcomingPayments';
import OrderDetails from '@/ts/store/modules/OrderDetails';
import CreateRefundForm from '@/ts/store/modules/CreateRefundForm';
import Channels from '@/ts/store/modules/Channels/Channels';

import cache from '@/plugins/cache';
import sync from '@/plugins/sync';

import axios from 'axios';

export interface RootState {
    [key: string]: any;
    initialized: boolean;
}

Vue.use(Vuex);

const store: StoreOptions<RootState> = {
    state: {
        initialized: false,
        setUserData: false,
    },
    mutations: {
        loadFromCache(state, cached) {

            if (cached && cached.User && cached.User.authData) {
                state.User.authData = cached.User.authData;

                axios.defaults.headers.common.Authorization =
                    'Bearer ' + cached.User.authData.accessToken;

                state.setUserData = true;
            }

            if (cached && cached.User && cached.User.identity) {
                axios.defaults.headers.common['Hococo-Context'] = cached.User.identity.context;
                axios.defaults.headers.common['Hococo-User-Type'] = cached.User.identity.userType;

                state.Identities.selectedIdentity = cached.User.identity;
            }

            if (cached && cached.User && cached.User.email) {
                state.User.email = cached.User.email;
            }

            if (cached && cached.User && cached.User.savedFullName) {
                state.User.savedFullName = cached.User.savedFullName;
            }

            if (cached && cached.User && cached.User.selectedLanguage) {
                state.User.selectedLanguage = cached.User.selectedLanguage;
            }

            if (cached && cached.User && cached.User.corporationDefaultLanguage) {
                state.User.corporationDefaultLanguage = cached.User.corporationDefaultLanguage;
            }

            state.initialized = true;
        },
    },
    plugins: [
        searchPlugin({
            resources: {},
        }),
        cache,
        sync,
    ],
    modules: {
        Messages,
        User,
        Camera,
        Images,
        Viewport,
        Notifications,
        Attachments,
        Events,
        Options,
        Documents,
        Tags,
        Categories,
        Socket,
        Feedback,
        Corporations,
        CorporationLinks,
        Projects,
        Buildings,
        UserManagement,
        Properties,
        Variants,
        Editor,
        Tickets,
        TicketAttributes,
        WaitingLists,
        EventLogs,
        UserTickets,
        PresentationFlows,
        Integrations,
        UserProfiles,
        ConfirmPopup,
        Localization,
        StaffProperties,
        SharedFacilities,
        RolesAndPermissions,
        Recipients,
        UnitFinance,
        CreditCards,
        PropertyNotes,
        Likes,
        Groupings,
        TableConfigs,
        UnitGroupingCharts,
        TemplateRenderer,
        UnpaidPayments,
        CreateDepositForm,
        CreateRefundForm,
        CorpTransactions,
        HomeAssistant,
        IssueReport,
        UserTicketsAttributes,
        UserWallet,
        FinanceControl,
        UserUpcomingPayments,
        OrderDetails,
        Loading,
        FileUpload,
        Comments,
        Identities,
        Overlays,
        GlobalOverlay,
        Scroll,
        TenantNotifications,
        Abuse,
        Channels,
    },
};

export default new Vuex.Store(store);
